import { memo, useCallback, useMemo, useState } from 'react';

import { Button } from '@/shadcn/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/shadcn/dialog';

import type {
  TableConfigRaw,
  TableRow
} from '@/components/ui/table/table.type';
import { getEditedPath, Widget } from '@/components/ui/widget';
import type { CoreViewType } from '@/components/ui/widget/views/views.type';
import type {
  WidgetType,
  WidgetUpdate
} from '@/components/ui/widget/widget.type';
import { setAtPath } from '@/utils';
import { executeStringFn } from '@/utils/executeFn';

type EditableCell = {
  key: string;
  header: string;
  widget: WidgetType;
  editedPath: string;
};

type PatchData = Record<string, Partial<TableRow>>;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedRows: string[];
  columns: TableConfigRaw['columns'];
  onSubmit: (
    patchData: PatchData,
    editablePaths: CoreViewType['path'][]
  ) => void;
};

const DEFAULT_PATCH: PatchData = {};

export const BatchEditModal = memo(
  ({ open, onOpenChange, selectedRows, columns, onSubmit }: Props) => {
    const [modalPatch, setModalPatch] = useState<PatchData>(DEFAULT_PATCH);

    const editableCells = useMemo(
      () =>
        columns.reduce((prevEditableCells, column) => {
          const { cell = {} as WidgetType, cellFn, header, key } = column;
          let finalCell = cell;
          if (cellFn) {
            const res = executeStringFn<PatchData, WidgetType>(
              modalPatch,
              cellFn
            );

            if (res) finalCell = res;
          }

          const editedPath = getEditedPath(finalCell);
          if (editedPath !== null) {
            prevEditableCells.push({
              key,
              header,
              widget: finalCell,
              editedPath
            });
          }
          return prevEditableCells;
        }, [] as EditableCell[]),
      [columns, modalPatch]
    );

    const editableCellsUpdates: WidgetUpdate[] = useMemo(
      () =>
        editableCells.map(cell => {
          const { header } = cell;

          return {
            patch: modalPatch,
            onChange: dataToChange => {
              const { path: modifiedDataPath, value: modifiedDataValue } =
                dataToChange;

              const newModalPatch = {
                ...modalPatch
              };

              // if value is empty ("") unset the patch at this path
              // ie batch won't update selected rows at this path
              setAtPath(
                newModalPatch,
                modifiedDataPath,
                modifiedDataValue === '' || modifiedDataValue === null
                  ? undefined
                  : modifiedDataValue
              );

              // update modal patch
              setModalPatch(newModalPatch);
            },
            editorOverrides: {
              header
            }
          };
        }),
      [editableCells, modalPatch]
    );

    const submitPatch = useCallback(() => {
      onSubmit(
        modalPatch,
        editableCells.map(cell => cell.editedPath)
      );
      setModalPatch(DEFAULT_PATCH);
    }, [editableCells, modalPatch]);

    return (
      <Dialog onOpenChange={onOpenChange} open={open}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              Edit the{' '}
              {selectedRows.length > 1
                ? `${selectedRows.length} selected rows`
                : 'selected row'}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Typing a value will update the corresponding cell in all selected
              rows.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col flex-nowrap text-sm">
            <div className="flex flex-col flex-nowrap gap-4">
              {editableCells.map((cell, cellIdx) => {
                const { key, widget } = cell;
                return (
                  <div className="relative" key={key}>
                    <Widget
                      // @ts-expect-error widget is not yet typed
                      data={{}}
                      update={editableCellsUpdates[cellIdx]}
                      widget={widget}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter className="mt-2 flex w-full justify-between">
            <DialogClose asChild>
              <Button
                aria-label="Cancel bulk edition"
                data-cy="cancel-batch-edit"
                type="reset"
                variant="outline"
              >
                Cancel
              </Button>
            </DialogClose>

            <Button
              data-cy="confirm-batch-edit"
              disabled={Object.keys(modalPatch).length === 0}
              onClick={submitPatch}
            >
              Edit selected rows
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
);
